import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { useSelectedYear } from '../year/useYear'
import { formatMoney, formatPeriod } from '../lib/format'
import {
  computeWaterfall, chargesInForce, type RecurringCharge,
} from '../lib/waterfall'
import { Loading, ErrorState } from '../components/States'


interface YearSummary {
  revenuNet: number
  chargesTotal: number
  chargesConfigured: boolean
  expected: number
  received: number
  balance: number
  months: number
  expensesApproved: number
  expensesPending: number
  unitsWithRent: number
}

/**
 * Resume de L'ANNEE consultee : ce qui etait attendu, ce qui est
 * rentre, et l'ecart. Les chiffres ne portent que sur les douze
 * periodes de cette annee.
 */
async function loadSummary(periods: string[]): Promise<YearSummary> {
  const [rent, expenses, charges] = await Promise.all([
    supabase
      .from('v_rent_status')
      .select('period, unit_id, amount_due, amount_received, balance, covered_by_advance')
      .in('period', periods),
    supabase
      .from('expenses')
      .select('period, amount, status')
      .in('period', periods),
    // Les charges mensuelles peuvent ne pas encore etre configurees :
    // l'accueil ne doit pas tomber en panne pour autant.
    supabase
      .from('recurring_charges')
      .select(
        'code, label_fr, label_en, kind, rate, fixed_amount, base, payee,' +
          ' requires_receipt, sort_order, a_verifier,' +
          ' effective_from, effective_to, active',
      ),
  ])

  if (rent.error) throw rent.error
  if (expenses.error) throw expenses.error

  const lignes = (rent.data ?? []) as {
    period: string
    unit_id: string
    amount_due: number
    amount_received: number
    balance: number
    covered_by_advance: boolean
  }[]

  const moisPresents = new Set(lignes.map((l) => l.period))
  const unites = new Set(lignes.map((l) => l.unit_id))

  const depenses = (expenses.data ?? []) as {
    period: string; amount: number; status: string
  }[]

  // Revenu net de l'annee : mois par mois, car une charge peut changer
  // de taux en cours d'annee. Calculer sur le total annuel donnerait
  // un chiffre faux ce mois-la.
  type ChargeRow = RecurringCharge & {
    effective_from: string; effective_to: string | null; active: boolean
    label_fr: string
  }
  const toutesCharges = (charges.error ? [] : (charges.data ?? [])) as unknown as ChargeRow[]

  let revenuNet = 0
  let chargesTotal = 0
  for (const p of periods) {
    const encaisse = lignes
      .filter((l) => l.period === p)
      .reduce((s, l) => s + l.amount_received, 0)
    const approuveMois = depenses
      .filter((e) => e.period === p && e.status === 'approuve')
      .reduce((s, e) => s + e.amount, 0)
    const enForce = chargesInForce(toutesCharges, p).map((c) => ({
      ...c,
      label: c.label_fr,
      rate: c.rate === null ? null : Number(c.rate),
    }))
    const w = computeWaterfall(encaisse, enForce, approuveMois)
    revenuNet += w.netToDeposit
    chargesTotal += w.recurringTotal
  }

  return {
    revenuNet,
    chargesTotal,
    chargesConfigured: toutesCharges.length > 0,
    // Un mois couvert par le mois d'avance n'attend aucun
    // encaissement : il ne gonfle donc pas l'attendu.
    expected: lignes
      .filter((l) => !l.covered_by_advance)
      .reduce((s, l) => s + l.amount_due, 0),
    received: lignes.reduce((s, l) => s + l.amount_received, 0),
    balance: lignes
      .filter((l) => !l.covered_by_advance)
      .reduce((s, l) => s + l.balance, 0),
    months: moisPresents.size,
    unitsWithRent: unites.size,
    expensesApproved: depenses
      .filter((e) => e.status === 'approuve')
      .reduce((s, e) => s + e.amount, 0),
    expensesPending: depenses
      .filter((e) => e.status === 'en_attente')
      .reduce((s, e) => s + e.amount, 0),
  }
}

// onNavigate accepte n'importe quel chemin : l'accueil renvoie aussi
// vers des ecrans qui ne sont pas des onglets, comme les rapports.
export function HomeScreen({ onNavigate }: { onNavigate: (to: string) => void }) {
  const { t, lang } = useI18n()
  const { profile } = useAuth()
  const { year, periods } = useSelectedYear()
  const { data, loading, error, reload } = useAsync(
    () => loadSummary(periods),
    [year],
  )

  const roleLabel = profile?.role
    ? t(`role_${profile.role}` as 'role_proprietaire' | 'role_gerant' | 'role_assistant')
    : ''

  return (
    <div className="screen">
      <h1 className="screen__title">{year}</h1>
      <p className="muted">{t('home_year_scope', { year })}</p>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {data && data.months === 0 && (
        <div className="card">
          <h2>{t('home_year_empty')}</h2>
          <p className="muted">{t('home_year_empty_body', { year })}</p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onNavigate('/loyers')}
          >
            {t('rent_open_month')}
          </button>
        </div>
      )}

      {data && data.months > 0 && (
        <>
          <div className="totals">
            <div>
              <span className="totals__label">{t('rent_total_expected')}</span>
              <span className="totals__value">{formatMoney(data.expected)}</span>
            </div>
            <div>
              <span className="totals__label">{t('rent_total_received')}</span>
              <span className="totals__value totals__value--accent">
                {formatMoney(data.received)}
              </span>
            </div>
          </div>

          {/* LE REVENU NET DE L'ANNEE : loyers encaisses, moins les
              charges mensuelles, moins les depenses approuvees. C'est
              le chiffre qui reste, et il est calcule mois par mois car
              une charge peut changer de taux en cours d'annee. */}
          <button
            type="button"
            className="card card--tappable"
            onClick={() => onNavigate('/rapports')}
          >
            <span className="card__caption">{t('report_net_revenue')}</span>
            <span className="card__figure">{formatMoney(data.revenuNet)}</span>
            <span className="muted">
              {formatMoney(data.received)} − {formatMoney(data.chargesTotal)} −{' '}
              {formatMoney(data.expensesApproved)}
            </span>
            <span className="muted">{t('home_revenue_detail')}</span>
          </button>

          {!data.chargesConfigured && (
            <div className="banner banner--warn">
              <strong>{t('home_no_charges')}</strong>
              <span>{t('home_no_charges_body')}</span>
            </div>
          )}

          {/* L'ecart est le chiffre qui compte : ce qui manque encore. */}
          <div className="card">
            <span className="card__caption">{t('rent_balance')}</span>
            <span className="card__figure card__figure--small">
              {formatMoney(data.balance)}
            </span>
            <p className="muted">
              {t('home_months_open', { n: data.months })} ·{' '}
              {t('units_count', { n: data.unitsWithRent })}
            </p>
          </div>

          <div className="totals">
            <div>
              <span className="totals__label">{t('expense_total_approved')}</span>
              <span className="totals__value">
                {formatMoney(data.expensesApproved)}
              </span>
            </div>
            <div>
              <span className="totals__label">{t('expense_total_pending')}</span>
              <span className="totals__value totals__value--warn">
                {formatMoney(data.expensesPending)}
              </span>
            </div>
          </div>
        </>
      )}

      <ul className="list">
        <li>
          <button
            type="button"
            className="list__item list__item--tappable"
            onClick={() => onNavigate('/loyers')}
          >
            <span className="list__main">
              <span className="list__name">{t('nav_rent')}</span>
              <span className="list__meta">
                {formatPeriod(`${year}-01`, lang)} — {formatPeriod(`${year}-12`, lang)}
              </span>
            </span>
            <span className="list__side">→</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            className="list__item list__item--tappable"
            onClick={() => onNavigate('/immeuble')}
          >
            <span className="list__main">
              <span className="list__name">{t('units_title')}</span>
            </span>
            <span className="list__side">→</span>
          </button>
        </li>
      </ul>

      <div className="card">
        {/* Nom issu de la base, jamais traduit. */}
        <h2>{profile?.full_name ?? ''}</h2>
        <p className="muted">{roleLabel}</p>
      </div>
    </div>
  )
}
